import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type UserStatus = 'online' | 'away' | 'offline';

export interface OnlineUser {
    user_id: string;
    last_seen: string;
}

let presenceChannel: RealtimeChannel | null = null;
let currentUserId: string | null = null;
const statusListeners = new Set<(statuses: Map<string, UserStatus>) => void>();

function notifyAll(statuses: Map<string, UserStatus>) {
    statusListeners.forEach(fn => fn(statuses));
}

const AWAY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

function calculateStatus(lastSeen: string): UserStatus {
    const diff = Date.now() - new Date(lastSeen).getTime();
    if (diff < AWAY_THRESHOLD_MS) return 'online';
    return 'away';
}

export function initGlobalPresence(userId: string, name: string, avatarUrl: string | null) {
    if (presenceChannel) return;
    currentUserId = userId;

    presenceChannel = supabase.channel('global:presence', {
        config: { presence: { key: userId } }
    });

    presenceChannel.on('presence', { event: 'sync' }, () => {
        const state = presenceChannel!.presenceState();
        const statuses = new Map<string, UserStatus>();

        for (const presences of Object.values(state)) {
            for (const p of presences as any[]) {
                const status = calculateStatus(p.last_seen || p.online_at);
                statuses.set(p.user_id, status);
            }
        }

        notifyAll(statuses);
    });

    presenceChannel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
            await presenceChannel!.track({
                user_id: userId,
                name,
                avatar_url: avatarUrl,
                last_seen: new Date().toISOString()
            });
            
            setInterval(async () => {
                await presenceChannel?.track({
                    user_id: userId,
                    name,
                    avatar_url: avatarUrl,
                    last_seen: new Date().toISOString()
                });
            }, 60000);
        }
    });
}

export function useOnlineStatus(userId: string | null): UserStatus {
    const [status, setStatus] = useState<UserStatus>('offline');

    useEffect(() => {
        if (!userId) {
            setStatus('offline');
            return;
        }

        const handleUpdate = (statuses: Map<string, UserStatus>) => {
            setStatus(statuses.get(userId) || 'offline');
        };

        statusListeners.add(handleUpdate);
        return () => { statusListeners.delete(handleUpdate); };
    }, [userId]);

    return status;
}

export function getStatusColor(status: UserStatus): string {
    switch (status) {
        case 'online': return 'bg-green-500';
        case 'away': return 'bg-yellow-500';
        case 'offline': return 'bg-gray-500';
    }
}